import { AdControls } from '@post-office/components--ad-controls';

import { cssMap, cx } from '@atlaskit/css';

import Image from '@atlaskit/image';

import { Anchor } from '@atlaskit/primitives/compiled/anchor';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Stack } from '@atlaskit/primitives/stack';

import { Text } from '@atlaskit/primitives/text';

import { token } from '@atlaskit/tokens';

import { type NextBestAction } from '@post-office/confluence-next-best-action';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useMessageEvent } from '@post-office/message-lifecycle';

import ChromeIcon from '../../../../../assets/chrome-icon.svg';

import RovoIcon from '../../../../../assets/rovo-icon.svg';

import { FormattedMessage, useIntl } from 'react-intl';

import { registeredContent } from '../messages';

import { type JSX } from 'react/jsx-runtime';

const styles = cssMap({
	boxContainer: {
		position: 'relative',
		display: 'flex',
		width: '100%',
		paddingTop: token('space.150', '12px'),
		paddingBottom: token('space.150', '12px'),
		paddingLeft: token('space.200', '16px'),
		paddingRight: token('space.200', '16px'),
		alignItems: 'flex-start',
		gap: token('space.100', '8px'),
		borderRadius: token('radius.large', '8px'),
		backgroundColor: token('color.background.accent.blue.subtlest'),
		placeContent: 'center',
	},
	contentStack: {
		gap: token('space.100', '8px'),
		width: '100%',
	},
	// Menu is pinned to the card's top-right corner, per the nav ads design
	adControls: {
		position: 'absolute',
		top: token('space.150', '12px'),
		right: token('space.150', '12px'),
	},
	// The nav ads Figma link is plain color.link text at 12px with no resting underline; the ADS
	// Link component can't be restyled (no xcss/className), so style the Anchor primitive instead.
	ctaLink: {
		alignSelf: 'flex-start',
		font: token('font.body.small'),
		lineHeight: '14px',
		color: token('color.link'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
	// Figma Heading/XS: 14px, 16px line-height, weight 600. ADS Heading (xsmall) is 14px/20px/653 and
	// can't be overridden, so render an h5 (same element Heading uses for xsmall) with the same font token.
	heading: {
		marginTop: token('space.0'),
		marginBottom: token('space.0'),
		font: token('font.heading.xsmall'),
		fontWeight: token('font.weight.semibold'),
		lineHeight: '16px',
		color: token('color.text'),
	},
	iconContainer: {
		display: 'flex',
		alignItems: 'center',
	},
});

// The icon assets are 24px canvases: a rounded 20px tile with a 16px image inside. The Figma shows the
// Rovo glyph at 22px, so scale each canvas by 22/16 (24px -> 33px). The tile, padding, rounded corners
// and the 8px overlap of the Chrome icon keep their proportions; they are just bigger.
const ICON_SCALE = 22 / 16;
const ICON_CANVAS_SIZE = 24 * ICON_SCALE;
const ICON_OVERLAP = -8 * ICON_SCALE;

// The left nav CTA experiment shipped copy-version-c. Kept as a static value so analytics and the
// link's utm_content keep reporting the same cohort name as before.
const EXPERIMENT_COHORT = 'copy-version-c';

const getLinkUrl = () => {
	const UTM_CONTENT = `side_nav_install_cta_${EXPERIMENT_COHORT}`;
	return `https://chromewebstore.google.com/detail/rovo-search-chat-and-more/npbjhobkibekbklkghlhfhieggcggpdb?utm_source=confluence-side-nav&utm_medium=banner&utm_campaign=rovo_extension_side_nav_install_cta&utm_content=${UTM_CONTENT}`;
};

type LeftNavCtaMessageProps = {
	handleOnDismissed: () => void;
	onSideNavClick: (nextBestActionType: NextBestAction) => string | void | null;
};

export const LeftNavCtaMessage = ({
	handleOnDismissed,
	onSideNavClick,
}: LeftNavCtaMessageProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { messageClicked } = useMessageEvent();
	const { onClosed, onCtaActioned } = usePostOfficeUserInteractions();

	const headingText = formatMessage(registeredContent.default.heading);
	const descriptionText = formatMessage(registeredContent.default.description);
	const linkText = formatMessage(registeredContent.default.linkText);
	const rovoIconAlt = formatMessage(registeredContent.default.rovoIconAlt);
	const chromeIconAlt = formatMessage(registeredContent.default.chromeIconAlt);

	const linkUrl = getLinkUrl();

	const handleDismiss = () => {
		void messageClicked();
		void onClosed({
			interactionIdentifier: 'dismiss',
			additionalMetadata: {
				experimentCohort: EXPERIMENT_COHORT,
				source: 'confluence-side-nav',
			},
		});
		handleOnDismissed();
	};

	const handleLinkClick = () => {
		void messageClicked();
		void onCtaActioned({
			interactionIdentifier: 'installLink',
			destinationUrl: linkUrl,
			additionalMetadata: {
				experimentCohort: EXPERIMENT_COHORT,
				source: 'confluence-side-nav',
			},
		});
		void onSideNavClick('ROVO_EXTENSION_CTA');
	};

	return (
		<Box xcss={cx(styles.boxContainer)}>
			<Stack xcss={cx(styles.contentStack)} testId="rovo-left-nav-content">
				<Box xcss={cx(styles.iconContainer)}>
					<Image
						src={RovoIcon}
						alt={rovoIconAlt}
						width={ICON_CANVAS_SIZE}
						height={ICON_CANVAS_SIZE}
						style={{ flexShrink: 0, zIndex: 2 }}
					/>
					<Image
						src={ChromeIcon}
						alt={chromeIconAlt}
						width={ICON_CANVAS_SIZE}
						height={ICON_CANVAS_SIZE}
						style={{ marginLeft: ICON_OVERLAP, flexShrink: 0, zIndex: 1 }}
					/>
				</Box>
				<Box as="h5" xcss={cx(styles.heading)}>
					{headingText}
				</Box>
				<Text size="small" color="color.text.subtlest">
					{descriptionText}
				</Text>
				<Anchor href={linkUrl} target="_blank" onClick={handleLinkClick} xcss={cx(styles.ctaLink)}>
					{linkText}
				</Anchor>
			</Stack>
			<Box xcss={cx(styles.adControls)}>
				<AdControls
					showWhyAmISeeingThis={true}
					whyAmISeeingThisModalContent={{
						reasons: [
							<FormattedMessage
								key="reason-1"
								{...registeredContent.default.whyAmISeeingThisReason}
							/>,
						],
					}}
					onDismissed={handleDismiss}
					spacing="compact"
				/>
			</Box>
		</Box>
	);
};
